import { useState, useEffect } from "react";
import { getVisitRecommendation } from "../utils/getVisitRecommendation";
import { useLanguage } from "../context/LanguageContext";
import { sectionPath } from "../utils/routePaths";

const FEATURED_LOCATIONS = [
  { id: "mestia", name: "Mestia", lat: 43.0503, lng: 42.7268 },
  { id: "ushguli", name: "Ushguli", lat: 43.1058, lng: 43.0542 },
  {
    id: "koruldi",
    name: "Koruldi Lakes",
    lat: 43.068,
    lng: 42.753,
    weatherRules: { winterAccessible: false },
  },
  {
    id: "tetnuldi",
    name: "Tetnuldi",
    lat: 43.0417,
    lng: 42.9167,
    weatherRules: { minTemperature: 2, maxWindSpeed: 20 },
  },
];

const STATUS_ICON = {
  excellent: "sentiment_very_satisfied",
  good: "thumb_up",
  caution: "warning",
  not_recommended: "cancel",
  no_data: "location_off",
};

const DOT_COLOR = {
  excellent: "#2d7a4f",
  good: "#2d7a4f",
  caution: "#b45309",
  not_recommended: "#ba1a1a",
  no_data: "var(--text-secondary)",
};

function MiniCard({ location }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    getVisitRecommendation(location)
      .then(setResult)
      .catch(() =>
        setResult({
          status: "no_data",
          label: "Unavailable",
          color: "var(--text-secondary)",
          weather: null,
          reasons: [],
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div
        style={{
          borderRadius: 16,
          border: "1px solid var(--border)",
          background: "var(--bg-surface)",
          padding: "16px 18px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      >
        <div
          style={{
            height: 13,
            width: "50%",
            background: "var(--bg-surface-alt)",
            borderRadius: 4,
            marginBottom: 14,
          }}
        />
        <div
          style={{
            height: 40,
            width: "35%",
            background: "var(--bg-surface-alt)",
            borderRadius: 4,
            marginBottom: 14,
          }}
        />
        <div
          style={{
            height: 12,
            width: "70%",
            background: "var(--bg-surface-alt)",
            borderRadius: 4,
          }}
        />
      </div>
    );

  const dotColor = DOT_COLOR[result?.status] || "var(--text-secondary)";

  const weatherEmoji = () => {
    const desc = (result?.weather?.description || "").toLowerCase();
    if (desc.includes("sun") || desc.includes("clear")) return "☀️";
    if (desc.includes("cloud") && desc.includes("sun")) return "⛅";
    if (desc.includes("cloud")) return "☁️";
    if (desc.includes("rain")) return "🌧️";
    if (desc.includes("snow")) return "❄️";
    if (desc.includes("thunder")) return "⛈️";
    if (desc.includes("fog") || desc.includes("mist")) return "🌫️";
    return "🌤️";
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: "1px solid var(--border)",
        background: "var(--bg-surface)",
        padding: "22px 24px",
        boxShadow: "0 1px 8px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s, transform 0.2s",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 8px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Location name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 14,
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 15, color: "var(--text-secondary)" }}
        >
          location_on
        </span>
        <span
          style={{
            fontFamily: "Geist, sans-serif",
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          {location.name}
        </span>
      </div>

      {/* Temperature + emoji */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <span
          style={{
            fontFamily: "Geist, sans-serif",
            fontSize: "clamp(32px, 6vw, 52px)",
            fontWeight: 700,
            color: "var(--text-primary)",
            lineHeight: 1,
          }}
        >
          {result?.weather?.temperature !== undefined
            ? `${result.weather.temperature}°`
            : "—"}
        </span>
        <span style={{ fontSize: 40 }}>{weatherEmoji()}</span>
      </div>

      {/* Status dot + label + description */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: dotColor,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: "Geist, sans-serif",
              fontSize: 13,
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {result?.label || t("live_unavailable")}
          </span>
        </div>
        {result?.reasons?.[0] && (
          <p
            style={{
              fontFamily: "Geist, sans-serif",
              fontSize: 12,
              color: "var(--text-secondary)",
              margin: "0 0 0 14px",
            }}
          >
            {result.reasons[0]}
          </p>
        )}
      </div>

      {/* Wind + rain */}
      {result?.weather && (
        <div
          style={{
            display: "flex",
            gap: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--border-soft)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16, color: "var(--text-secondary)" }}
            >
              air
            </span>
            <div>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                {result.weather.windSpeed} km/h
              </div>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  color: "var(--text-secondary)",
                }}
              >
                {result.weather.windDirection || "NW"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16, color: "var(--text-secondary)" }}
            >
              water_drop
            </span>
            <div>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                {result.weather.rainProbability}%
              </div>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  color: "var(--text-secondary)",
                }}
              >
                {t ? t("live_rain") || "ნალექი" : "ნალექი"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LiveSvanetiConditions() {
  const { language, t } = useLanguage();

  return (
    <section style={{ background: "transparent" }}>
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          boxSizing: "border-box",
        }}
        className="live-conditions-inner"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 40,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 12 C3 12 5 7 9 7 C11 7 11 9 13 9 C15 9 15 5 18 5 C20 5 21 8 21 8"
                  stroke="#2d7a4f"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
              <span
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#2d7a4f",
                }}
              >
                {t("live_eyebrow")}
              </span>
            </div>
            <h2
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                margin: "0 0 10px",
              }}
            >
              {t("live_title")}
            </h2>
            <p
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: 15,
                color: "var(--text-secondary)",
                margin: 0,
              }}
            >
              {language === "ka"
                ? "ამინდის პროგნოზი პოპულარული ლოკაციებისთვის"
                : "Weather forecast for popular locations"}
            </p>
          </div>
          <a
            href={sectionPath(language, "categories")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              border: "1.5px solid var(--border)",
              color: "var(--text-primary)",
              padding: "12px 22px",
              borderRadius: 999,
              fontFamily: "Geist, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              whiteSpace: "nowrap",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-inverse)";
              e.currentTarget.style.color = "var(--fg-inverse)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--text-primary)";
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16 }}
            >
              location_on
            </span>
            {language === "ka" ? "ყველა ლოკაციის ნახვა" : "View all locations"}
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16 }}
            >
              arrow_forward
            </span>
          </a>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
          }}
          className="live-conditions-grid"
        >
          {FEATURED_LOCATIONS.map((loc) => (
            <MiniCard key={loc.id} location={loc} />
          ))}
        </div>

        {/* Bottom info bar */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 12,
            marginTop: 20,
          }}
          className="live-info-bar"
        >
          {[
            {
              icon: "landscape",
              label: language === "ka" ? "საუკეთესო დრო" : "Best Season",
              value: language === "ka" ? "მოსანახულებლად" : "To Visit",
              sub:
                language === "ka" ? "ივნისი - სექტემბერი" : "June - September",
              green: true,
            },
            {
              icon: "visibility",
              label: language === "ka" ? "ხილვადობა" : "Visibility",
              value: language === "ka" ? "შესანიშნავი" : "Excellent",
              sub: "10+ km",
              green: false,
            },
            {
              icon: "eco",
              label: language === "ka" ? "ჰაერის ხარისხი" : "Air Quality",
              value: language === "ka" ? "უმაღლესი" : "Excellent",
              sub: "AQI 25",
              green: false,
            },
            {
              icon: "directions_car",
              label: language === "ka" ? "გზის მდგომარეობა" : "Road Conditions",
              value: language === "ka" ? "კარგი" : "Good",
              sub: language === "ka" ? "ყველა გზა ღია" : "All roads open",
              green: false,
            },
            {
              icon: "security",
              label: language === "ka" ? "უსაფრთხოების დონე" : "Safety Level",
              value: language === "ka" ? "მაღალი" : "High",
              sub:
                language === "ka"
                  ? "უსაფრთხო მოგზაურობისთვის"
                  : "Safe for travel",
              green: true,
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background: "var(--bg-surface-alt)",
                borderRadius: 14,
                padding: "16px 18px",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 22,
                  color: item.green ? "#2d7a4f" : "var(--text-secondary)",
                  display: "block",
                  marginBottom: 8,
                }}
              >
                {item.icon}
              </span>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  color: "var(--text-secondary)",
                  marginBottom: 2,
                }}
              >
                {item.label}
              </div>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  marginBottom: 2,
                }}
              >
                {item.value}
              </div>
              <div
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  color: "var(--text-secondary)",
                }}
              >
                {item.sub}
              </div>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "var(--text-secondary)",
            marginTop: 16,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
            info
          </span>
          {t("live_footer")}
        </p>
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }

        .live-conditions-inner { padding: 96px 64px; }
        .live-conditions-grid { grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .live-info-bar { grid-template-columns: repeat(5, 1fr); gap: 12px; margin-top: 20px; }

        @media (max-width: 1024px) {
          .live-conditions-inner { padding: 64px 32px !important; }
          .live-conditions-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .live-info-bar { grid-template-columns: repeat(3, 1fr) !important; }
        }

        @media (max-width: 640px) {
          .live-conditions-inner { padding: 48px 16px !important; }
          .live-conditions-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .live-info-bar { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
        }

        @media (max-width: 400px) {
          .live-conditions-grid { grid-template-columns: 1fr !important; }
          .live-info-bar { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  );
}
